<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

const store = useStore<RootState>();

defineOptions({
  name: 'dataLanguages',
});

// Базовый тип для сообщений с ID языка
type LanguageWithId = {
  idLanguage: number;
};

// Базовый тип для create/update операций языков
type LanguageCreateUpdateData = LanguageWithId & {
  code: string;
  selfTitle: string;
  rtl: boolean;
};

// Типы для WebSocket сообщений компонента languages
type LanguageRootMessage = WebSocketMessageWithFields<'language-root' | 'language-locale-list' | 'profile-main' | 'profile-locale-details', {
  language: unknown;
}>;
type LanguageRootNotFoundMessage = WebSocketMessageEmpty<'language-root-not-found' | 'language-locale-list-not-found'>;
type LanguageListMessage = WebSocketMessageWithFields<'language-list' | 'language-country-name' | 'language-country-list' | 'language-switcher-data' | 'language-action-error-translations' | 'language-email-template-translations' | 'language-entity-translations' | 'language-group-item-translations' | 'language-page-translations' | 'language-page-item-list-translations', {
  languages: unknown[];
}>;
type LanguageCreateMessage = WebSocketMessageWithFields<'language-create' | 'language-create-success', LanguageCreateUpdateData>;
type LanguageUpdateMessage = WebSocketMessageWithFields<'language-update' | 'language-update-success', LanguageCreateUpdateData>;
type LanguageDeleteMessage = WebSocketMessageWithFields<'language-delete' | 'language-delete-success', LanguageWithId>;
type LanguageLockMessage = WebSocketMessageWithFields<'language-lock' | 'language-lock-success', LanguageWithId>;
type LanguageUnlockMessage = WebSocketMessageWithFields<'language-unlock' | 'language-unlock-success', LanguageWithId>;

// Объединенный тип для всех сообщений
type LanguagesWebSocketMessage = 
  | LanguageRootMessage 
  | LanguageRootNotFoundMessage 
  | LanguageListMessage 
  | LanguageCreateMessage 
  | LanguageUpdateMessage 
  | LanguageDeleteMessage 
  | LanguageLockMessage 
  | LanguageUnlockMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: LanguagesWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'language-root':
    case 'language-locale-list':
    case 'profile-main':
    case 'profile-locale-details':
      store.dispatch('language/wsSetLanguage', data.language);
      break;
    case 'language-root-not-found':
    case 'language-locale-list-not-found':
      store.dispatch('language/wsSetEmptyLanguage');
      break;
    case 'language-list':
    case 'language-country-name':
    case 'language-country-list':
    case 'language-switcher-data':
    case 'language-action-error-translations':
    case 'language-email-template-translations':
    case 'language-entity-translations':
    case 'language-group-item-translations':
    case 'language-page-translations':
    case 'language-page-item-list-translations':
      store.dispatch('languages/wsSetLanguages', {languages: data.languages});
      break;
    case 'language-create':
    case 'language-create-success':
      store.dispatch('languages/wsLanguageCreate', {
        idLanguage: data.idLanguage,
        code: data.code,
        selfTitle: data.selfTitle,
        rtl: data.rtl,
      });
      break;
    case 'language-update':
    case 'language-update-success':
      store.dispatch('language/wsLanguageUpdate', data);
      store.dispatch('languages/wsLanguageUpdate', {
        idLanguage: data.idLanguage,
        code: data.code,
        selfTitle: data.selfTitle,
        rtl: data.rtl,
      });
      break;
    case 'language-delete':
    case 'language-delete-success':
      store.dispatch('language/wsLanguageDelete', data);
      store.dispatch('languages/wsLanguageDelete', {
        idLanguage: data.idLanguage,
      });
      break;
    case 'language-lock':
    case 'language-lock-success':
      store.dispatch('language/wsLanguageLock', data);
      store.dispatch('languages/wsLanguageLock', {
        idLanguage: data.idLanguage,
      });
      break;

    case 'language-unlock':
    case 'language-unlock-success':
      store.dispatch('language/wsLanguageUnlock', data);
      store.dispatch('languages/wsLanguageUnlock', {
        idLanguage: data.idLanguage,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
